/* ============================================
   useChatNotifications — Global chat notification system
   
   Features:
   1. Sound notification on every incoming message
   2. Browser Notification API when tab is not visible
   3. Dynamic favicon badge (green dot) when tab hidden + unread
   4. Total unread count for sidebar badge

   Los chats silenciados quedan fuera de los cuatro: no suenan, no notifican,
   no marcan el favicon y no suman al contador. Su propia píldora de no leídos
   sigue en la lista — silenciar no es marcar como leído.
   ============================================ */
import { useEffect, useRef, useCallback, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { puedeVerRuta } from '@/lib/modulos'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'
import {
  cargarSilenciadas,
  estaSilenciada,
  suscribirSilenciadas,
} from '@/lib/conversaciones-silenciadas'
import type { Mensaje } from '@/lib/types'

// ─── Notification sound using Web Audio API ───
function playNotificationSound() {
  try {
    const ctx = new AudioContext()
    const now = ctx.currentTime

    // Two-tone chime: pleasant "ding-dong" at moderate volume
    const frequencies = [880, 1100] // A5, C#6
    const durations = [0.12, 0.18]

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, now)

      const start = now + i * 0.15
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(0.15, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.001, start + durations[i])

      osc.start(start)
      osc.stop(start + durations[i] + 0.05)
    })

    // Auto-close context after sound finishes
    setTimeout(() => ctx.close(), 1000)
  } catch {
    // AudioContext not available — silent fail
  }
}

// ─── Dynamic favicon with notification badge ───
const ORIGINAL_FAVICON_HREF = '/favicon.svg'
let faviconCanvas: HTMLCanvasElement | null = null
let originalFaviconImg: HTMLImageElement | null = null
let faviconLoaded = false

function getFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  return link
}

function loadOriginalFavicon(): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (originalFaviconImg && faviconLoaded) {
      resolve(originalFaviconImg)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      originalFaviconImg = img
      faviconLoaded = true
      resolve(img)
    }
    img.onerror = reject
    img.src = ORIGINAL_FAVICON_HREF
  })
}

async function setFaviconWithBadge(showBadge: boolean) {
  const link = getFaviconLink()

  if (!showBadge) {
    // Restore original SVG favicon
    link.type = 'image/svg+xml'
    link.href = ORIGINAL_FAVICON_HREF
    return
  }

  try {
    const img = await loadOriginalFavicon()

    if (!faviconCanvas) {
      faviconCanvas = document.createElement('canvas')
    }
    const size = 64
    faviconCanvas.width = size
    faviconCanvas.height = size
    const ctx = faviconCanvas.getContext('2d')
    if (!ctx) return

    // Draw original favicon
    ctx.clearRect(0, 0, size, size)
    ctx.drawImage(img, 0, 0, size, size)

    // Draw green notification dot (top-right corner)
    const dotRadius = 10
    const dotX = size - dotRadius - 2
    const dotY = dotRadius + 2

    // White border for contrast
    ctx.beginPath()
    ctx.arc(dotX, dotY, dotRadius + 2, 0, Math.PI * 2)
    ctx.fillStyle = '#ffffff'
    ctx.fill()

    // Green dot
    ctx.beginPath()
    ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2)
    ctx.fillStyle = '#22c55e'
    ctx.fill()

    // Update favicon
    link.type = 'image/png'
    link.href = faviconCanvas.toDataURL('image/png')
  } catch {
    // Fallback: keep original favicon
  }
}

// ─── Browser Notification API ───
async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

function showBrowserNotification(message: Mensaje) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return

  const body = message.tipo === 'texto'
    ? (message.contenido ?? 'Nuevo mensaje')
    : `📎 ${message.tipo.charAt(0).toUpperCase() + message.tipo.slice(1)}`

  const notification = new Notification('1Conecta — Nuevo mensaje', {
    body,
    icon: '/assets/brand/logo-icon-color.svg',
    tag: `chat-msg-${message.id}`, // Prevents duplicate notifications
    silent: true, // We handle our own sound
  })

  // Click notification → focus tab
  notification.onclick = () => {
    window.focus()
    notification.close()
  }

  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000)
}

// ─── Title flash effect ───
let titleInterval: ReturnType<typeof setInterval> | null = null
const ORIGINAL_TITLE = '1Conecta ERP'

function startTitleFlash(count: number) {
  stopTitleFlash()
  let showCount = true
  titleInterval = setInterval(() => {
    document.title = showCount ? `(${count}) 💬 Nuevo mensaje` : ORIGINAL_TITLE
    showCount = !showCount
  }, 1500)
}

function stopTitleFlash() {
  if (titleInterval) {
    clearInterval(titleInterval)
    titleInterval = null
  }
  document.title = ORIGINAL_TITLE
}

// ─── Main Hook ───
export function useChatNotifications() {
  const [unreadCount, setUnreadCount] = useState(0)
  const pendingWhileHidden = useRef(0)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Una sesión sin el Inbox no tiene contador que refrescar. Sin esto la
  // consulta sale igual y vuelve 403 en cada evento y en cada vez que la
  // pestaña recupera el foco: no rompe nada —está atrapado— pero le deja la
  // consola llena de errores a alguien que no tiene nada que arreglar.
  const { modulos } = useAuth()
  const hayInbox = puedeVerRuta(modulos, '/conversaciones')

  // Debounced unread count refresh — avoids rapid successive queries
  const refreshUnreadCount = useCallback(() => {
    if (!hayInbox) return
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('conversaciones')
          .select('id, mensajes_no_leidos')

        if (data) {
          const total = (data as any[]).reduce((sum: number, c: any) => {
            const row = c as { id: number; mensajes_no_leidos: number }
            // Silenciado = fuera del contador. Es el punto de silenciar un grupo
            // ruidoso: que el badge del sidebar deje de gritar.
            if (estaSilenciada(row.id)) return sum
            return sum + (row.mensajes_no_leidos || 0)
          }, 0)
          setUnreadCount(total)
        }
      } catch { /* non-critical */ }
    }, 500)
  }, [hayInbox])

  // Track tab visibility
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        // Tab became visible → clear all indicators
        pendingWhileHidden.current = 0
        setFaviconWithBadge(false)
        stopTitleFlash()
        // Refresh count when user returns
        refreshUnreadCount()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [refreshUnreadCount])

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission()
  }, [])

  // Load initial unread count
  useEffect(() => {
    refreshUnreadCount()
  }, [refreshUnreadCount])

  // Chats silenciados: cargar la lista y recontar cuando el usuario silencia o reactiva uno
  useEffect(() => {
    cargarSilenciadas().then(refreshUnreadCount)
    return suscribirSilenciadas(refreshUnreadCount)
  }, [refreshUnreadCount])

  // Handle incoming message notification
  const handleIncomingMessage = useCallback((msg: Mensaje, conversacionId: number | null) => {
    // Only notify for incoming messages (from contacts, not our own)
    if (msg.direccion !== 'entrante') return

    // Chat silenciado: el contador se refresca igual (el no leído del chat sube),
    // pero acá se corta todo lo que interrumpe.
    if (conversacionId != null && estaSilenciada(conversacionId)) {
      refreshUnreadCount()
      return
    }

    // Always play sound
    playNotificationSound()

    // Update unread count (debounced — the DB counter increments via webhook)
    refreshUnreadCount()

    // If tab is hidden, show browser notification + favicon badge + title flash
    if (document.hidden) {
      pendingWhileHidden.current += 1
      showBrowserNotification(msg)
      setFaviconWithBadge(true)
      startTitleFlash(pendingWhileHidden.current)
    }
  }, [refreshUnreadCount])

  // Realtime SSE: new messages trigger notifications
  useRealtimeEvent('new_message', useCallback((data: any) => {
    const msg = data?.mensaje as Mensaje
    const convId = Number(data?.conversacion_id ?? msg?.conversacion_id)
    if (msg) handleIncomingMessage(msg, isNaN(convId) ? null : convId)
  }, [handleIncomingMessage]))

  // Realtime SSE: conversation updates refresh unread count
  useRealtimeEvent('conversation_updated', useCallback(() => {
    refreshUnreadCount()
  }, [refreshUnreadCount]))

  return { unreadCount }
}
